import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { omit } from "lodash-es";
import type {
  MapGeoJSONFeature,
  MapMouseEvent,
  MapSourceDataEvent,
  Source,
} from "maplibre-gl";
import { useMap } from "react-map-gl/maplibre";

import { usePopupMenu } from "@/features/app/PopupMenuProvider";
import {
  getColorFromFeatureType,
  getIconFromFeatureType,
  getTextFromFeatureType,
} from "@/features/editor/components/overlay_layers/OverlayLayersConstants";
import { LayerSelectionPopupItem } from "@/features/map/components/layer_selection_popup/LayerSelectionPopupItem";
import type { EventFeature } from "@/features/map/hooks/events/types";
import type {
  BaseEventCallbackRegistration,
  EventLayer,
  MapInteractionsContextType,
  MaplibreEventCallback,
  MaplibreEventData,
  MaplibreEventType,
} from "@/features/map/MapInteractionsProvider.types";
import type { ModelOverlayFeature } from "../editor/types";
import { useDeckGlMap } from "./DeckGlMapProvider";
import FeatureAdapterFactory from "./hooks/events/mappers";

// Constants
const DEFAULT_PICKING_RADIUS = 10;
export enum EventPriority {
  DEFAULT = 0,
  MEDIUM = 1,
  HIGH = 2,
  HIGHEST = 3,
  GEOJSON_EDITOR = 1000,
  GEOJSON_EDITOR_HIGHEST = 1001,
  // Nothing can be higher than this, it is used by the event handler
  HANDLER = Number.MAX_SAFE_INTEGER,
}

const MapInteractionsContext = createContext<MapInteractionsContextType | null>(
  null,
);
export const useMapInteractions = () => useContext(MapInteractionsContext);

export const MapInteractionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Refs
  const callbacksRef = useRef<{
    [event in MaplibreEventType]?: {
      [layer in EventLayer]?: BaseEventCallbackRegistration[];
    };
  }>({});

  const _maplibreListenersRef = useRef<{
    [event in MaplibreEventType]?: {
      [layer in EventLayer]?: (e: any) => void;
    };
  }>({});

  // Keep track of datasources readyness
  const _sourceQueryReadyRef = useRef<Record<Source["id"], boolean>>({});
  const _pendingSubscriptionsRef = useRef<
    Record<
      Source["id"],
      {
        [event in MaplibreEventType]?: {
          [layer in EventLayer]?: BaseEventCallbackRegistration[];
        };
      }
    >
  >({});

  // Track pending map events that need to be processed after layer events
  const pendingMapEventsRef = useRef<{
    [eventId: string]: {
      type: MaplibreEventType;
      layer: EventLayer;
      event: MapMouseEvent;
      timestamp: number;
    }[];
  }>({});

  // Track threejs scenes
  const threeJSInstaces = useRef<Record<string, ModelOverlayFeature[]>>({});

  // Custom hooks
  const { map: deckMap } = useDeckGlMap();
  const { map: maplibreMap } = useMap();
  const { openPopupMenu } = usePopupMenu();

  /***************************************************/
  // Helpers
  /***************************************************/

  /**
   * Queues a subscription until its GeoJSON source is query-ready.
   * Dedupes by callback reference per source, type, and layer.
   */
  const addPendingSubscription = ({
    sourceId,
    type,
    layer,
    cb,
    priority,
  }: {
    sourceId: Source["id"];
    type: MaplibreEventType;
    layer: EventLayer;
    cb: MaplibreEventCallback;
    priority: number;
  }) => {
    // Initialize
    if (!_pendingSubscriptionsRef.current[sourceId]) {
      _pendingSubscriptionsRef.current[sourceId] = {};
    }
    const pendingBySource = _pendingSubscriptionsRef.current[sourceId];
    if (!pendingBySource[type]) {
      pendingBySource[type] = {};
    }
    if (!pendingBySource[type]![layer]) {
      pendingBySource[type]![layer] = [];
    }

    // Store new unique callback
    const pendingCallbacks = pendingBySource[type]![layer]!;
    if (pendingCallbacks.some((entry) => entry.cb === cb)) {
      return;
    }
    pendingCallbacks.push({ cb, priority });
    pendingCallbacks.sort((a, b) => b.priority - a.priority);
  };

  /**
   * Removes a queued subscription by callback reference.
   */
  const removePendingSubscription = ({
    sourceId,
    type,
    layer,
    cb,
  }: {
    sourceId: Source["id"];
    type: MaplibreEventType;
    layer: EventLayer;
    cb: MaplibreEventCallback;
  }): boolean => {
    const pendingCallbacks =
      _pendingSubscriptionsRef.current[sourceId]?.[type]?.[layer];
    if (!pendingCallbacks) {
      return;
    }

    const index = pendingCallbacks.findIndex((entry) => entry.cb === cb);
    if (index === -1) {
      return;
    }

    pendingCallbacks.splice(index, 1);

    // Remove storage object if there are no remaining callbacks for the
    // given event source, event type or layer
    if (pendingCallbacks.length === 0) {
      delete _pendingSubscriptionsRef.current[sourceId][type]![layer];

      if (
        Object.keys(_pendingSubscriptionsRef.current[sourceId][type] || {})
          .length === 0
      ) {
        delete _pendingSubscriptionsRef.current[sourceId][type];
      }

      if (
        Object.keys(_pendingSubscriptionsRef.current[sourceId]).length === 0
      ) {
        delete _pendingSubscriptionsRef.current[sourceId];
      }
    }
  };

  /**
   * Activates all queued subscriptions for a source via `subscribe`.
   */
  const flushPendingSubscriptions = (sourceId: Source["id"]) => {
    const pendingBySource = _pendingSubscriptionsRef.current[sourceId];
    if (!pendingBySource) {
      return;
    }

    // Clear the pending queue for that source before subscribing again
    delete _pendingSubscriptionsRef.current[sourceId];

    // Subscribe all pending callbacks for the given source
    for (const type of Object.keys(pendingBySource) as MaplibreEventType[]) {
      const layers = pendingBySource[type];
      if (!layers) continue;

      for (const layer of Object.keys(layers) as EventLayer[]) {
        const registrations = layers[layer];
        if (!registrations) continue;

        for (const { cb, priority } of [...registrations]) {
          subscribe(type, layer, cb, priority);
        }
      }
    }
  };

  // Generate a unique event ID based on event type and coordinates
  const generateEventId = (type: MaplibreEventType, event: any) => {
    return `${type}_${event.point.x}_${event.point.y}`;
  };

  /**
   * Marks one event as pending, so that if other events are triggered with higher
   * priority in a small window of time, this event will be processed after them.
   */
  const addPendingMapEvent = (
    type: MaplibreEventType,
    layer: EventLayer,
    event: any,
  ) => {
    const eventId = generateEventId(type, event);
    // Prevent default to avoid the map's own behavior
    event.preventDefault?.();
    if (!pendingMapEventsRef.current[eventId]) {
      pendingMapEventsRef.current[eventId] = [];
    }
    pendingMapEventsRef.current[eventId].push({
      event: { ...event }, // Create a copy to avoid mutating the original event
      layer,
      timestamp: Date.now(),
      type,
    });
  };

  /**
   * Process pending events sorted by priority and call `fireEvent` if needed.
   * ⚠️ Prevents propagation of the original event.
   */
  const processPendingMapEvents = (eventId: string) => {
    const pendingEvents = pendingMapEventsRef.current[eventId];
    // Remove the event from the pending events
    delete pendingMapEventsRef.current[eventId];

    if (pendingEvents) {
      pendingEvents
        .sort((a, b) => {
          const highestPriorityA = callbacksRef.current[a.type]?.[a.layer].sort(
            (a, b) => b.priority - a.priority,
          )[0].priority;
          const highestPriorityB = callbacksRef.current[b.type]?.[b.layer].sort(
            (a, b) => b.priority - a.priority,
          )[0].priority;
          return highestPriorityB - highestPriorityA;
        })
        .some((pendingEvent) => {
          const returnValue = fireEvent(
            pendingEvent.type,
            pendingEvent.layer,
            pendingEvent.event,
          );
          return returnValue;
        });
    }
  };

  /***************************************************/
  // Callback registration
  /***************************************************/

  /**
   * Register a callback for a specific event type and layer.
   *
   * @param type type of event to register for
   * @param layer layer type
   * @param cb callback function to register
   */
  const subscribe: MapInteractionsContextType["subscribe"] = (
    type,
    layer,
    cb,
    priority = 0,
  ) => {
    if (!maplibreMap) return;
    const layerKey = layer ?? "map";

    // Check if source data is ready before subscribing any event
    // If not, store event as pending and subscribe when data is marked as ready
    const sourceId = layer ? maplibreMap.getLayer(layer)?.source : null;
    if (sourceId && _sourceQueryReadyRef.current[sourceId] === false) {
      addPendingSubscription({ cb, layer, priority, sourceId, type });
      return;
    }

    // Initialize
    if (!callbacksRef.current[type]) {
      callbacksRef.current[type] = {};
    }
    if (!callbacksRef.current[type][layerKey]) {
      callbacksRef.current[type][layerKey] = [];
    }

    // Find previous callbacks for the given event on the given layer
    const existingCallbacks = callbacksRef.current[type][layerKey];
    existingCallbacks.push({ cb, priority });
    existingCallbacks.sort((a, b) => b.priority - a.priority);

    // If this is the first callback, we create the top-level listener
    // that handles all pending callbacks
    if (existingCallbacks.length === 1) {
      const listener = (event: any) => {
        // For click events, we store the event in a pending queue and process
        // all of them at once, sorted by priority of its listeners.
        if (type === "click" || type === "contextmenu") {
          const eventId = generateEventId(type, event);
          addPendingMapEvent(type, layer, event);
          setTimeout(() => processPendingMapEvents(eventId), 10);
          return;
        }

        // For press/drag-like events (e.g., mousedown), process synchronously.
        // If any callback returns true, prevent default to stop map's own behavior.
        const handled = fireEvent(type, layer, event as any);
        if (handled) {
          event?.preventDefault?.();
        }
      };

      if (layerKey === "map" || type === "touchmove") {
        maplibreMap.on(type, listener);
      } else {
        maplibreMap.on(type, layer, listener);
      }

      // Store the listener reference so we can remove it later
      if (!_maplibreListenersRef.current[type]) {
        _maplibreListenersRef.current[type] = {};
      }
      _maplibreListenersRef.current[type]![layerKey] = listener;
    }
  };

  /**
   * Unregister a callback for a specific event type and layer.
   *
   * @param type type of event to unregister for
   * @param layer layer type
   * @param cb callback function to unregister
   */
  const unsubscribe: MapInteractionsContextType["unsubscribe"] = (
    type,
    layer,
    cb,
  ) => {
    if (!maplibreMap) return;
    const layerKey = layer ?? "map";
    const sourceId = layer ? maplibreMap.getLayer(layer)?.source : null;

    // Remove from pending subscriptions if it exists
    if (sourceId && _pendingSubscriptionsRef.current[sourceId]) {
      removePendingSubscription({ cb, layer: layerKey, sourceId, type });
    }

    const existingCallbacks = callbacksRef.current[type]?.[layerKey];
    if (!existingCallbacks) return;
    const index = existingCallbacks.findIndex((item) => item.cb === cb);
    if (index !== -1) {
      existingCallbacks.splice(index, 1);
    }

    // If no callbacks remain, remove the map event listener
    if (existingCallbacks.length === 0) {
      // Remove from MapLibre if it's the last listener
      if (_maplibreListenersRef.current[type]?.[layerKey]) {
        if (layerKey === "map" || type === "touchmove") {
          maplibreMap.off(
            type,
            _maplibreListenersRef.current[type]![layerKey]!,
          );
        } else {
          maplibreMap.off(
            type,
            layer,
            _maplibreListenersRef.current[type]![layerKey]!,
          );
        }
        delete _maplibreListenersRef.current[type]![layerKey];

        if (
          Object.keys(_maplibreListenersRef.current[type] || {}).length === 0
        ) {
          delete _maplibreListenersRef.current[type];
        }
      }
    }
  };

  /**
   * Fire event map to all registered callbacks.
   *
   * @param type type of event to fire
   * @param layer layer related to this event
   * @param data data for the given event type
   * @returns true if the event was handled (a callback returned true), false otherwise
   */
  const fireEvent = <T extends EventLayer | null>(
    type: MaplibreEventType,
    layer: T,
    data: MapMouseEvent & {
      features?: MapGeoJSONFeature[];
    },
  ) => {
    const layerKey = layer ?? "map";
    const eventCallbacks = callbacksRef.current[type]?.[layerKey];
    if (!eventCallbacks) return false;

    const threeJSFeatures = FeatureAdapterFactory.createThreeJSAdapter({
      instances: Object.values(threeJSInstaces.current).flat(),
      map: maplibreMap,
      options: {
        layerIds: [layerKey],
      },
      point: {
        x: data.point.x,
        y: data.point.y,
      },
    }).map();

    const maplibreFeatures = FeatureAdapterFactory.createMapLibreAdapter({
      features: data.features,
      map: maplibreMap,
      options: {
        layerIds: [layerKey],
      },
    }).map();

    const modifiedData = omit(data, "features") as MaplibreEventData;
    modifiedData.features = [...threeJSFeatures, ...maplibreFeatures];

    return eventCallbacks.some((entry) => entry.cb(modifiedData) === true);
  };

  /***************************************************/
  // Map interaction utils
  /***************************************************/

  const pickFeatures: MapInteractionsContextType["pickFeatures"] = useCallback(
    ({ point, options }) => {
      if (!deckMap?.current || !maplibreMap) return [];
      const deckglFeatures = FeatureAdapterFactory.createDeckGlAdapter({
        map: deckMap,
        options,
        point,
      }).map();

      const maplibreFeatures = FeatureAdapterFactory.createMapLibreAdapter({
        map: maplibreMap,
        options,
        point,
      }).map();

      const threeJSFeatures = FeatureAdapterFactory.createThreeJSAdapter({
        instances: Object.values(threeJSInstaces.current).flat(),
        map: maplibreMap,
        point: {
          x: point.x,
          y: point.y,
        },
      }).map();

      return [...maplibreFeatures, ...deckglFeatures, ...threeJSFeatures];
    },
    [maplibreMap, deckMap],
  );

  /**
   * Pick features both from deckGL and maplibre on a given point.
   *   If singleFeature is true, it will prompt the user to choose if multiple features are present in the same position
   *   If featureTypes is set, only features of the given types will be checked
   *
   * @param canvasX canvas X position
   * @param canvasY canvas Y position
   * @param options optional options to personalize the picking process
   * @returns list of GenericOverlayFeatures
   */
  const pickFeaturesInteractive: MapInteractionsContextType["pickFeaturesInteractive"] =
    async ({ point, options = {}, screenPoint }): Promise<EventFeature[]> => {
      const pickedFeatures = pickFeatures({
        options,
        point,
      });

      // Return a promise that is fulfilled when a feature is picked
      return new Promise((resolve) => {
        // If only one feature is allowed and we have more than one, display the popup.
        if (options.singleFeature && pickedFeatures.length > 1) {
          openPopupMenu(
            pickedFeatures.map((obj) => {
              return (
                <LayerSelectionPopupItem
                  key={`layer-selection-popup-item-${obj.id}`}
                  icon={getIconFromFeatureType(obj.properties.type)}
                  name={obj.properties.name ?? `ID '${obj.id}'`}
                  featureType={getTextFromFeatureType(obj.properties.type)}
                  featureColor={getColorFromFeatureType(obj.properties.type)}
                  onClick={() => resolve([obj])}
                />
              );
            }),
            screenPoint,
          );
        } else {
          resolve(pickedFeatures);
        }
      });
    };

  /**
   * For a given canvas position, call pickMultipleObjects
   */
  const pickDeckGlRawObjects: MapInteractionsContextType["pickDeckGlRawObjects"] =
    (canvasX, canvasY, options = {}) => {
      if (!deckMap?.current) return [];
      const deckRawObjects = deckMap.current.pickMultipleObjects({
        layerIds: options.layerIds,
        radius: DEFAULT_PICKING_RADIUS,
        x: canvasX,
        y: canvasY,
      });

      return deckRawObjects;
    };

  /**
   * Include ThreeJS instances information to integrate all event functionality
   */
  const addThreeJSScene: MapInteractionsContextType["addThreeJSScene"] = (
    sourceId,
    instances,
  ) => {
    threeJSInstaces.current[sourceId] = instances;
  };

  /**
   * Remove ThreeJS instances to not account for them anymore on events
   */
  const removeThreeJSScene: MapInteractionsContextType["removeThreeJSScene"] = (
    sourceId,
  ) => {
    delete threeJSInstaces.current[sourceId];
  };

  // Watch source load state and flush queued layer subscriptions
  // when ready. This avoids an issue within maplibre where
  // subscriptions relying on data (e.g. click events) end up failing
  // because the source data was not ready when the subscription was created
  // Note: this only affect Maplibre layers
  useEffect(() => {
    if (!maplibreMap) return;

    const handleSourceDataChange = (event: MapSourceDataEvent) => {
      const sourceId = event.sourceId;
      if (!sourceId) return;

      // Set as ready when not network request is pending
      // and when tile has been resolved
      _sourceQueryReadyRef.current[sourceId] =
        event.isSourceLoaded && event.tile?.state === "loaded";

      // If data is loaded, subscribe all pending events
      if (_sourceQueryReadyRef.current[sourceId]) {
        flushPendingSubscriptions(sourceId);
      }
    };

    maplibreMap.on("sourcedata", handleSourceDataChange);

    return () => {
      maplibreMap.off("sourcedata", handleSourceDataChange);
    };
  }, [maplibreMap]);

  return (
    <MapInteractionsContext.Provider
      value={{
        addThreeJSScene,
        pickDeckGlRawObjects,
        pickFeatures,
        pickFeaturesInteractive,
        removeThreeJSScene,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </MapInteractionsContext.Provider>
  );
};
